/** Owner/admin/superadmin "view as EliteBot": the chat panel flips to the bot's inbox —
 *  every DM employees have with the bot, read-only, with the bot's bubbles on
 *  the right as if you were him. Nothing here can send, delete or mark read. */
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Bot, Loader2, Minus, X } from "lucide-react";
import api from "@/lib/api";
import Avatar from "./Avatar";
import { fetchBotConversations, fetchBotMessages } from "./chatApi";

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
};
const stamp = (iso) => new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function BotThread({ convo, bot, onBack, onMinimize, onClose }) {
  const [msgs, setMsgs] = useState(null);
  const endRef = useRef(null);
  useEffect(() => {
    let alive = true;
    const load = () => fetchBotMessages(convo.conversation_id).then((d) => alive && setMsgs(d)).catch(() => alive && setMsgs([]));
    load();
    const t = setInterval(load, 6000);
    return () => { alive = false; clearInterval(t); };
  }, [convo.conversation_id]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs?.length]);

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="bot-inbox-thread">
      <div className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2">
        <button onClick={onBack} data-testid="bot-inbox-back" aria-label="Back" className="rounded-full p-1.5 text-gray-600 hover:bg-amber-100"><ArrowLeft className="h-4 w-4" /></button>
        <Avatar name={convo.name} picture={convo.picture} showStatus={false} size={32} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{convo.name}</p>
          <p className="truncate text-[10px] uppercase tracking-wider text-amber-700">Reading as {bot.name} · read-only</p>
        </div>
        <button onClick={onMinimize} aria-label="Minimize chat" className="rounded-full p-1.5 text-gray-600 hover:bg-amber-100"><Minus className="h-4 w-4" /></button>
        <button onClick={onClose} aria-label="Close chat" className="rounded-full p-1.5 text-gray-600 hover:bg-amber-100"><X className="h-4 w-4" /></button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 px-3 py-3">
        {msgs === null && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-gray-300" /></div>}
        {msgs?.length === 0 && <p className="py-10 text-center text-xs text-gray-400">Nothing said here yet.</p>}
        {msgs?.map((m) => {
          const mine = m.sender_id === bot.user_id;
          return (
            <div key={m.message_id} className={`mb-2 flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`bot-inbox-msg-${m.message_id}`}>
              <div className={`max-w-[80%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                {m.deleted ? (
                  <div className="rounded-2xl border border-gray-200 px-3 py-1.5 text-xs italic text-gray-400">Message deleted</div>
                ) : (
                  <div className={`rounded-2xl px-3 py-1.5 text-sm leading-snug ${mine ? "bg-[#1a3fd1] text-white" : "bg-white text-gray-900 shadow-sm"}`}>
                    {m.attachment && <p className="text-xs italic opacity-80">[{m.attachment.kind || "attachment"}: {m.attachment.name || "file"}]</p>}
                    {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
                  </div>
                )}
                <span className="mt-0.5 text-[10px] text-gray-400">{mine ? bot.name : (m.sender_name || convo.name).split(" ")[0]} · {stamp(m.created_at)}</span>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-2 text-center text-[11px] text-gray-400" data-testid="bot-inbox-readonly">
        You're reading as the bot — replies here come from {bot.name} himself.
      </div>
    </div>
  );
}

export default function BotInboxView({ onExit, onMinimize, onClose }) {
  const [bot, setBot] = useState({ user_id: "user_elitebot", name: "EliteBot", picture: "/elitebot.svg" });
  const [convos, setConvos] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/ai/bot-profile").then((r) => setBot(r.data)).catch(() => {});
    let alive = true;
    const load = () => fetchBotConversations().then((d) => alive && setConvos(d)).catch(() => alive && setConvos([]));
    load();
    const t = setInterval(load, 8000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (active) return <BotThread convo={active} bot={bot} onBack={() => setActive(null)} onMinimize={onMinimize} onClose={onClose} />;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="bot-inbox">
      <div className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-3">
        <Avatar name={bot.name} picture={bot.picture} showStatus={false} size={36} />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-bold text-gray-900">{bot.name}'s inbox</h2>
          <p className="text-[10px] uppercase tracking-wider text-amber-700">Viewing as the bot · read-only</p>
        </div>
        <button onClick={onExit} data-testid="bot-inbox-exit" className="rounded-full bg-gray-900 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-gray-700">
          Back to my chats
        </button>
        <button onClick={onMinimize} aria-label="Minimize chat" className="rounded-full bg-amber-100 p-2 text-gray-600 hover:bg-amber-200"><Minus className="h-4 w-4" /></button>
        <button onClick={onClose} aria-label="Close chat" className="rounded-full bg-amber-100 p-2 text-gray-600 hover:bg-amber-200"><X className="h-4 w-4" /></button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pb-[max(env(safe-area-inset-bottom),0.5rem)]">
        {convos === null && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-gray-300" /></div>}
        {convos?.length === 0 && (
          <p className="px-6 py-10 text-center text-xs text-gray-400"><Bot className="mx-auto mb-2 h-6 w-6 text-gray-300" />Nobody has messaged {bot.name} yet.</p>
        )}
        {convos?.map((c) => (
          <button key={c.conversation_id} onClick={() => setActive(c)} data-testid={`bot-inbox-convo-${c.conversation_id}`}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50">
            <Avatar name={c.name} picture={c.picture} online={c.online} lastActive={c.peer_last_active} size={44} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-800">{c.name}</p>
              <p className="truncate text-xs text-gray-500">
                {c.last_sender_id === bot.user_id ? `${bot.name}: ` : ""}{c.last_message_preview || "—"}
                <span className="text-gray-400"> · {relTime(c.last_message_at)} · {c.message_count} msgs</span>
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
